import * as Tabs from '@radix-ui/react-tabs'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ComponentProps, MouseEvent } from 'react'
import { PointProgramText } from '~/components/PointProgram'
import { useMealPeriodTab } from '~/features/guide/mealPeriod'
import { MEAL_PERIOD_LABEL } from '~/lib/constants/constants'
import type { ReservableCalendarItemFragment } from '~/lib/graphql/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import type { MealPeriodTab } from '~/lib/mealPeriod/tab'
import { tabIcons } from '~/lib/mealPeriod/tab'
import { useReserveNavigate } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { TwoWeeksInventoryCalendar } from './TwoWeeksInventoryCalendar'

type Props = {
  groupedCalendar: [MealPeriodTab, ReservableCalendarItemFragment][]
  restaurantId: RestaurantId
  restaurant: ComponentProps<typeof TwoWeeksInventoryCalendar>['restaurant']
  showIkyuDayBalloon: boolean
}

const styles = sva({
  slots: ['trigger', 'icon'],
  base: {
    trigger: {
      display: 'flex',
      flex: '1',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: 'medium',
      paddingBottom: '3',
      color: 'tertiary',
      _hover: {
        opacity: '75',
      },
      '&[data-state="active"]': {
        borderBottom: 'tab.active',
        borderColor: 'secondary',
        fontWeight: 'bold',
        color: 'gray.800',
      },
    },
    icon: {
      width: '[13px]',
      height: '[13px]',
      marginRight: '1',
      color: 'secondary',
    },
  },
})

function MealPeriodsCalendarSectionClient({
  groupedCalendar,
  restaurantId,
  restaurant,
  showIkyuDayBalloon,
}: Props) {
  // 「最短で mm/dd に空席があります」の表示有無によってタブ内の要素の高さが変わるため、タブを切り替えても高さが変動しないように最大の高さを保持したい。
  const [maxTabContentHeight, setMaxTabContentHeight] = useState(0)

  const selectedTab = useMealPeriodTab(restaurantId)
  const activeTab = groupedCalendar.some(([tab]) => tab === selectedTab)
    ? selectedTab
    : groupedCalendar.length > 0
      ? groupedCalendar[0][0]
      : undefined

  const navigate = useReserveNavigate()
  const createTabTriggerHandler = useCallback(
    (tab: string) => {
      return function (_e: MouseEvent) {
        const mealPeriod = tab as MealPeriodTab
        navigate({ type: 'changeMealPeriod', mealPeriod })
      }
    },
    [navigate]
  )

  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    if (ref.current === null) {
      return
    }
    const contents = ref.current.querySelectorAll('[data-content]')
    const max = Math.max(...Array.from(contents).map((el) => el.getBoundingClientRect().height))
    setMaxTabContentHeight((current) => Math.max(current, max))
  }, [activeTab])

  const classes = styles()

  return (
    <>
      <section ref={ref}>
        {/* 在庫がなければタイトルも表示しない */}
        {groupedCalendar.length > 0 && (
          <div className={hstack({ gap: '2', justifyContent: 'space-between', p: '3' })}>
            <h3 className={css({ fontSize: 'lg', fontWeight: 'bold' })}>空席カレンダー</h3>
            <PointProgramText type="short" showIkyuDayBalloon={showIkyuDayBalloon} />
          </div>
        )}
        <nav className="w-full">
          <Tabs.Root value={activeTab}>
            <Tabs.List className="mb-4 flex items-center justify-between">
              {groupedCalendar.map(([tab, calendar]) => {
                const Icon = tabIcons[tab]
                return (
                  <Tabs.Trigger
                    key={tab}
                    value={tab}
                    onClick={createTabTriggerHandler(tab)}
                    className={classes.trigger}
                  >
                    <Icon className={classes.icon} />
                    {MEAL_PERIOD_LABEL[tab]}
                  </Tabs.Trigger>
                )
              })}
            </Tabs.List>
            {groupedCalendar.map(([tab, calendar]) => {
              return (
                <Tabs.Content
                  key={tab}
                  value={tab}
                  forceMount
                  className="data-[state=inactive]:hidden"
                >
                  <div data-content style={{ minHeight: `${maxTabContentHeight}px` }}>
                    <TwoWeeksInventoryCalendar
                      reservableCalendar={calendar}
                      restaurant={restaurant}
                    />
                  </div>
                </Tabs.Content>
              )
            })}
          </Tabs.Root>
        </nav>
      </section>
    </>
  )
}

export default MealPeriodsCalendarSectionClient
